import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  forwardRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  Validators,
} from '@angular/forms';
import { ICascadingConfig } from '../../models/irembo-cascading-config';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';

export interface EditableInputValueChange {
  status: boolean;
  previousValue: string | null;
  newValue: any;
  datasetValueName?: string;
  datasetValueCode?: string;
}

@Component({
  standalone: false,
  selector: 'irembogov-cascading-editable-input',
  templateUrl: './irembo-cascading-editable-input.component.html',
  styleUrls: ['../irembo-editable-input/irembo-editable-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IremboCascadingEditableInputComponent),
      multi: true,
    },
  ],
})
export class IremboCascadingEditableInputComponent
  implements ControlValueAccessor, OnInit, OnChanges, OnDestroy
{
  @Input() loading: Record<string, boolean> = {};
  @Input() configs: ICascadingConfig[] = [];
  @Input() items: Record<string, Record<string, unknown>[] | null> | undefined;
  @Input() isTouched = false;
  @Input() linear = false;
  @Input() placeholder = '';
  @Input() label?: string;
  @Input() formlyAttributes: any;

  @Output() activeDropDown = new EventEmitter<ICascadingConfig>();
  @Output() valueChange = new EventEmitter<{
    formValue: Record<string, any>;
    data: Record<string, any>;
  }>();

  form: FormGroup = new FormGroup({});
  editingStates: Record<string, boolean> = {};
  initialValues: Record<string, any> = {};
  mappedInitialValues: Record<string, any> = {};
  originalMappedValues: Record<string, any> = {};
  tempValues: Record<string, any> = {};
  pendingValues: Record<string, any> = {};
  savedValues: Record<string, any> = {};
  wasInitiallyEmpty: Record<string, boolean> = {};
  editedStates: Record<string, boolean> = {};
  fetchDataSubscriptions: Subscription[] = [];
  private readonly initiallyPopulatedFields = new Set<string>();
  private readonly initiallyNullFields = new Set<string>();
  private processingReset = false;
  private isSaving = false;
  private isInitialized = false;

  /* eslint-disable @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function*/
  private _onChange = (value: unknown): void => {};
  private _onTouch = (value: unknown): void => {};
  /* eslint-enable */

  constructor(
    private readonly cd: ChangeDetectorRef,
    private readonly http: HttpClient
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.loadAllCascadingData();
  }

  private initializeForm(): void {
    const group: Record<string, FormControl> = {};
    this.configs.forEach(field => {
      group[field.key] = field.required
        ? new FormControl(null, Validators.required)
        : new FormControl(null);
      this.editingStates[field.key] = false;
      this.editedStates[field.key] = false;
      this.tempValues[field.key] = null;
      this.pendingValues[field.key] = null;
      this.savedValues[field.key] = null;
      this.wasInitiallyEmpty[field.key] = true;
      this.initiallyNullFields.add(field.key);
    });
    this.form = new FormGroup(group);

    this.fetchDataSubscriptions.push(
      this.form.valueChanges.subscribe(value => {
        this._onChange(value);
        this._onTouch(value);
      })
    );
  }

  private loadAllCascadingData(): Promise<void> {
    const initialValue = this.formlyAttributes?.field?.formControl?.value ?? {};
    this.writeValue(initialValue);

    return this.configs.reduce((promise, config) => {
      return promise.then(() => {
        const parentKey = config.parent;
        const parentValue = parentKey ? this.form.get(parentKey)?.value : null;
        const rawInitialValue = this.initialValues[config.key];

        this.wasInitiallyEmpty[config.key] =
          rawInitialValue === undefined ||
          rawInitialValue === null ||
          (rawInitialValue &&
            typeof rawInitialValue === 'object' &&
            rawInitialValue.newValue === null);

        if (!this.wasInitiallyEmpty[config.key]) {
          this.initiallyNullFields.delete(config.key);
        }

        const control = this.form.get(config.key);
        let mappedValue = null;

        if (!this.wasInitiallyEmpty[config.key]) {
          mappedValue = this.mapInitialValue(config, rawInitialValue);
          if (!mappedValue && rawInitialValue) {
            mappedValue = this.cloneValue(rawInitialValue);
          }

          if (mappedValue && control) {
            control.setValue(mappedValue);
            this.mappedInitialValues[config.key] = this.cloneValue(mappedValue);
            this.originalMappedValues[config.key] =
              this.cloneValue(mappedValue);
            this.tempValues[config.key] = this.cloneValue(mappedValue);
            this.pendingValues[config.key] = this.cloneValue(mappedValue);
            this.savedValues[config.key] = this.cloneValue(mappedValue);
            this.initiallyPopulatedFields.add(config.key);
          } else if (control) {
            control.setValue(null);
            this.mappedInitialValues[config.key] = null;
            this.originalMappedValues[config.key] = null;
            this.tempValues[config.key] = null;
            this.pendingValues[config.key] = null;
            this.savedValues[config.key] = null;
          }
        } else if (control) {
          control.setValue(null);
          this.mappedInitialValues[config.key] = null;
          this.originalMappedValues[config.key] = null;
          this.tempValues[config.key] = null;
          this.pendingValues[config.key] = null;
          this.savedValues[config.key] = null;
        }

        const shouldEmitActiveDropDown =
          !config.parent ||
          (parentKey &&
            parentValue &&
            this.configs.some(
              cfg => cfg.key === parentKey && !this.wasInitiallyEmpty[cfg.key]
            ));

        if (shouldEmitActiveDropDown) {
          this.activeDropDown.emit({ ...config, value: parentValue });
        }

        return Promise.resolve();
      });
    }, Promise.resolve());
  }

  private cloneValue(value: any): any {
    if (!value) return null;
    try {
      return JSON.parse(JSON.stringify(value));
    } catch (error) {
      return value;
    }
  }

  private stripLeadingZeros(value: string): string {
    return String(value).replace(/^0+/, '') || '0';
  }

  private mapInitialValue(config: ICascadingConfig, initialValue: any): any {
    if (!Array.isArray(this.items?.[config.key])) {
      return null;
    }

    const items = this.items?.[config.key] ?? [];
    if (items.length === 0) {
      return null;
    }

    if (
      initialValue === null ||
      initialValue === undefined ||
      (initialValue &&
        typeof initialValue === 'object' &&
        initialValue.newValue === null)
    ) {
      return null;
    }

    const bindValueKey = config.bindvalue ?? 'id';
    const bindLabelKey = config.bindlabel ?? 'name';

    // Handle EditableInputValueChange format
    if (
      initialValue &&
      typeof initialValue === 'object' &&
      'newValue' in initialValue
    ) {
      let newValue = initialValue.newValue;
      // Handle nested EditableInputValueChange
      if (newValue && typeof newValue === 'object' && 'newValue' in newValue) {
        newValue = newValue.newValue;
      }
      if (!newValue) {
        return null;
      }

      return (
        items.find(item => {
          return (
            (item[bindValueKey] !== undefined &&
              newValue[bindValueKey] !== undefined &&
              this.stripLeadingZeros(String(item[bindValueKey])) ===
                this.stripLeadingZeros(String(newValue[bindValueKey]))) ||
            (item[bindLabelKey] !== undefined &&
              newValue[bindLabelKey] !== undefined &&
              String(item[bindLabelKey]) === String(newValue[bindLabelKey])) ||
            (item['value'] !== undefined &&
              newValue['value'] !== undefined &&
              this.stripLeadingZeros(String(item['value'])) ===
                this.stripLeadingZeros(String(newValue['value'])))
          );
        }) ?? null
      );
    }

    // Handle object values (non-EditableInputValueChange)
    if (initialValue && typeof initialValue === 'object') {
      return (
        items.find(item => {
          return (
            (item[bindValueKey] !== undefined &&
              initialValue[bindValueKey] !== undefined &&
              this.stripLeadingZeros(String(item[bindValueKey])) ===
                this.stripLeadingZeros(String(initialValue[bindValueKey]))) ||
            (item[bindLabelKey] !== undefined &&
              initialValue[bindLabelKey] !== undefined &&
              String(item[bindLabelKey]) ===
                String(initialValue[bindLabelKey])) ||
            (item['value'] !== undefined &&
              initialValue['value'] !== undefined &&
              this.stripLeadingZeros(String(item['value'])) ===
                this.stripLeadingZeros(String(initialValue['value'])))
          );
        }) ?? null
      );
    }

    // Handle string or number values
    if (typeof initialValue === 'string' || typeof initialValue === 'number') {
      const normalizedValue = this.stripLeadingZeros(String(initialValue));
      return (
        items.find(item => {
          if (
            bindValueKey &&
            this.stripLeadingZeros(String(item[bindValueKey] ?? '')) ===
              normalizedValue
          ) {
            return true;
          }
          if (String(item[bindLabelKey] ?? '') === String(initialValue)) {
            return true;
          }
          if (
            this.stripLeadingZeros(String(item['value'] ?? '')) ===
            normalizedValue
          ) {
            return true;
          }
          const normalizedLabel = this.normalizeValue(
            String(item[bindLabelKey] ?? '')
          );
          return normalizedLabel === this.normalizeValue(String(initialValue));
        }) || null
      );
    }

    return null;
  }

  private normalizeValue(value: string): string {
    if (!value || typeof value !== 'string') return '';
    return value.trim().toLowerCase();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['isTouched'] && this.form?.invalid) {
      this.form.markAllAsTouched();
    }

    if (changes?.['configs']) {
      this.updateValidationRulesForConfigsChange(
        changes['configs'].currentValue
      );
    }

    if (changes?.['items']) {
      this.updateFormWithItems(changes['items'].currentValue);
    }
  }

  private updateValidationRulesForConfigsChange(
    newConfigs: ICascadingConfig[]
  ): void {
    if (!this.form || !newConfigs) return;

    newConfigs.forEach(field => {
      const control = this.form.get(field.key);
      if (control) {
        if (field.required) {
          control.setValidators(Validators.required);
        } else {
          control.clearValidators();
        }
        control.updateValueAndValidity();
      }
    });
  }

  private updateFormWithItems(
    newItems: Record<string, Record<string, unknown>[] | null>
  ): void {
    if (!this.form || !newItems) return;

    this.configs.forEach(config => {
      const control = this.form.get(config.key);
      if (
        newItems[config.key] &&
        control?.value &&
        !this.initiallyPopulatedFields.has(config.key)
      ) {
        const mappedValue = this.mapInitialValue(config, control.value);
        if (mappedValue) {
          control.setValue(mappedValue);
          this.mappedInitialValues[config.key] = this.cloneValue(mappedValue);
          this.tempValues[config.key] = this.cloneValue(mappedValue);
          this.pendingValues[config.key] = this.cloneValue(mappedValue);
          this.savedValues[config.key] = this.cloneValue(mappedValue);
          if (!this.initiallyNullFields.has(config.key)) {
            this.originalMappedValues[config.key] =
              this.cloneValue(mappedValue);
            this.initiallyPopulatedFields.add(config.key);
          }
        }
      }
    });
  }

  hasParentValue(parentKey: string | null): boolean {
    if (!parentKey || !this.form) return true;
    const control = this.form.get(parentKey);
    return !!control?.value && control.value !== null;
  }

  isAnyFieldEditing(fieldKey: string): boolean {
    return Object.keys(this.editingStates).some(
      key => key !== fieldKey && this.editingStates[key] === true
    );
  }

  isEdited(fieldKey: string): boolean {
    return this.editedStates[fieldKey] === true;
  }

  isTopmostParent(field: ICascadingConfig): boolean {
    return !field.parent;
  }

  resetChildDropDowns(parentKey: string): void {
    if (!this.form) return;

    const childConfigs = this.configs.filter(
      config => config.parent === parentKey
    );

    childConfigs.forEach(childConfig => {
      const control = this.form.get(childConfig.key);
      if (control) {
        control.setValue(null);
        this.editedStates[childConfig.key] = false;
        this.tempValues[childConfig.key] = null;
        this.pendingValues[childConfig.key] = null;
        this.savedValues[childConfig.key] = null;
        this.resetChildDropDowns(childConfig.key);
      }
    });
  }

  onSelectChange(
    value: Record<string, unknown> | Array<Record<string, unknown>>,
    key: string
  ): void {
    if (!this.form || this.processingReset || !this.editingStates[key]) return;

    const configIndex = this.configs.findIndex(config => config.key === key);
    if (configIndex === -1) return;

    let normalizedValue;
    if (Array.isArray(value)) {
      normalizedValue = value.length > 0 ? value[0] : null;
    } else {
      normalizedValue = value;
    }

    this.pendingValues[key] = this.cloneValue(normalizedValue);

    const config = this.configs.find(cfg => cfg.key === key);
    if (config) {
      const tempValue = this.tempValues[key];
      this.editedStates[key] = this.isValueChanged(
        key,
        normalizedValue,
        tempValue,
        config
      );
    }

    this.cd.detectChanges();
  }

  startEdit(fieldKey: string): void {
    this.editingStates[fieldKey] = true;
    const control = this.form.get(fieldKey);
    const config = this.configs.find(cfg => cfg.key === fieldKey);
    if (control && config) {
      this.tempValues[fieldKey] = this.cloneValue(control.value);
      this.pendingValues[fieldKey] = this.cloneValue(control.value);
      control.markAsTouched();

      const parentKey = config.parent;
      const parentValue = parentKey ? this.form.get(parentKey)?.value : null;
      if (parentKey && parentValue) {
        this.activeDropDown.emit({ ...config, value: parentValue });
      }
    }
    this.cd.detectChanges();
  }

  cancelEdit(fieldKey: string): void {
    this.editingStates[fieldKey] = false;
    const control = this.form.get(fieldKey);
    if (control) {
      const tempValue = this.tempValues[fieldKey];
      control.setValue(tempValue);
      this.pendingValues[fieldKey] = this.cloneValue(tempValue);
      const config = this.configs.find(cfg => cfg.key === fieldKey);
      if (config) {
        const originalValue = this.wasInitiallyEmpty[fieldKey]
          ? null
          : this.originalMappedValues[fieldKey] ??
            this.mappedInitialValues[fieldKey];
        this.editedStates[fieldKey] = this.isValueChanged(
          fieldKey,
          tempValue,
          originalValue,
          config
        );
      }
    }
    this.cd.detectChanges();
  }

  resetToInitial(fieldKey: string): void {
    if (!this.form) return;

    const config = this.configs.find(config => config.key === fieldKey);
    if (!config) {
      return;
    }

    if (!this.isTopmostParent(config)) {
      return;
    }

    this.processingReset = true;
    this.editingStates[fieldKey] = false;
    this.editedStates[fieldKey] = false;

    let originalValue = this.originalMappedValues[fieldKey];
    let initialValue = this.initialValues[fieldKey];

    // Handle EditableInputValueChange
    if (
      initialValue &&
      typeof initialValue === 'object' &&
      'newValue' in initialValue &&
      'previousValue' in initialValue
    ) {
      // Extract previousValue for resetting
      const prevValue = initialValue.previousValue;
      initialValue = prevValue;

      // Update tracking flags based on the previousValue
      if (prevValue === null) {
        this.wasInitiallyEmpty[fieldKey] = true;
        this.initiallyNullFields.add(fieldKey);
      } else {
        this.wasInitiallyEmpty[fieldKey] = false;
        this.initiallyNullFields.delete(fieldKey);
      }
    }

    if (this.initiallyNullFields.has(fieldKey)) {
      originalValue = null;
    } else if (!originalValue && !this.wasInitiallyEmpty[fieldKey]) {
      if (initialValue === null || initialValue === undefined) {
        const items = this.items?.[fieldKey] ?? [];
        if (items.length > 0) {
          initialValue = items[0];
        }
      }
      if (initialValue !== null && initialValue !== undefined) {
        // Ensure items are available before mapping
        if (this.items?.[fieldKey]?.length) {
          originalValue = this.mapInitialValue(config, initialValue);
          if (!originalValue && initialValue) {
            originalValue = this.cloneValue(initialValue);
          }
          if (originalValue) {
            this.originalMappedValues[fieldKey] =
              this.cloneValue(originalValue);
            if (!this.initiallyPopulatedFields.has(fieldKey)) {
              this.initiallyPopulatedFields.add(fieldKey);
            }
          }
        } else {
          // Trigger fetch to ensure items are loaded
          const parentKey = config.parent;
          const parentValue = parentKey
            ? this.form.get(parentKey)?.value
            : null;
          this.activeDropDown.emit({ ...config, value: parentValue });
        }
      }
    }

    const control = this.form.get(fieldKey);
    if (control) {
      control.setValue(originalValue);
      this.tempValues[fieldKey] = this.cloneValue(originalValue);
      this.pendingValues[fieldKey] = this.cloneValue(originalValue);
      this.savedValues[fieldKey] = this.cloneValue(originalValue);
      this.mappedInitialValues[fieldKey] = this.cloneValue(originalValue);
    }

    const parentKey = config.parent;
    const parentValue = parentKey ? this.form.get(parentKey)?.value : null;

    this.activeDropDown.emit({ ...config, value: parentValue });

    const childConfigs = this.configs.filter(cfg => cfg.parent === fieldKey);
    childConfigs.forEach(childConfig => {
      if (originalValue || !childConfig.parent) {
        this.activeDropDown.emit({ ...childConfig, value: originalValue });
      }
    });

    setTimeout(() => {
      this.resetAllChildrenToInitial(fieldKey);
      this.processingReset = false;
      this.emitChange();
      this.cd.detectChanges();
    }, 100);
  }

  resetAllChildrenToInitial(parentKey: string): void {
    if (!this.form) return;

    const childConfigs = this.configs.filter(
      config => config.parent === parentKey
    );

    childConfigs.forEach(childConfig => {
      this.editingStates[childConfig.key] = false;
      this.editedStates[childConfig.key] = false;

      const control = this.form.get(childConfig.key);
      const parentValue = this.form.get(parentKey)?.value;

      if (control) {
        let originalChildValue = this.originalMappedValues[childConfig.key];
        let initialChildValue = this.initialValues[childConfig.key];

        // Handle EditableInputValueChange
        if (
          initialChildValue &&
          typeof initialChildValue === 'object' &&
          'newValue' in initialChildValue &&
          'previousValue' in initialChildValue
        ) {
          // Extract previousValue for resetting
          const prevValue = initialChildValue.previousValue;
          initialChildValue = prevValue;

          // Update tracking flags based on the previousValue
          if (prevValue === null) {
            this.wasInitiallyEmpty[childConfig.key] = true;
            this.initiallyNullFields.add(childConfig.key);
          } else {
            this.wasInitiallyEmpty[childConfig.key] = false;
            this.initiallyNullFields.delete(childConfig.key);
          }
        }

        if (this.initiallyNullFields.has(childConfig.key)) {
          originalChildValue = null;
        } else if (
          !originalChildValue &&
          !this.wasInitiallyEmpty[childConfig.key] &&
          initialChildValue !== undefined
        ) {
          if (initialChildValue === null || initialChildValue === undefined) {
            const items = this.items?.[childConfig.key] ?? [];
            if (items.length > 0) {
              initialChildValue = items[0];
            }
          }
          if (initialChildValue !== null && initialChildValue !== undefined) {
            if (this.items?.[childConfig.key]?.length) {
              originalChildValue = this.mapInitialValue(
                childConfig,
                initialChildValue
              );
              if (!originalChildValue && initialChildValue) {
                originalChildValue = this.cloneValue(initialChildValue);
              }
              if (originalChildValue) {
                this.originalMappedValues[childConfig.key] =
                  this.cloneValue(originalChildValue);
                if (!this.initiallyPopulatedFields.has(childConfig.key)) {
                  this.initiallyPopulatedFields.add(childConfig.key);
                }
              }
            } else {
              // Trigger fetch for child
              this.activeDropDown.emit({ ...childConfig, value: parentValue });
            }
          }
        }

        control.setValue(originalChildValue);
        this.tempValues[childConfig.key] = this.cloneValue(originalChildValue);
        this.pendingValues[childConfig.key] =
          this.cloneValue(originalChildValue);
        this.savedValues[childConfig.key] = this.cloneValue(originalChildValue);
        this.mappedInitialValues[childConfig.key] =
          this.cloneValue(originalChildValue);

        this.resetAllChildrenToInitial(childConfig.key);
      }
    });
  }

  saveEdit(fieldKey: string): void {
    if (!this.validateField(fieldKey)) return;

    this.isSaving = true;
    this.editingStates[fieldKey] = false;
    const control = this.form.get(fieldKey);
    const newValue = this.pendingValues[fieldKey];
    const config = this.configs.find(cfg => cfg.key === fieldKey);

    if (!config || !control) {
      this.isSaving = false;
      return;
    }

    control.setValue(newValue);
    this.savedValues[fieldKey] = this.cloneValue(newValue);

    const originalValue = this.wasInitiallyEmpty[fieldKey]
      ? null
      : this.originalMappedValues[fieldKey] ??
        this.mappedInitialValues[fieldKey];
    const isValueChanged = this.isValueChanged(
      fieldKey,
      newValue,
      originalValue,
      config
    );

    this.editedStates[fieldKey] = isValueChanged;

    if (isValueChanged && this.wasInitiallyEmpty[fieldKey]) {
      this.originalMappedValues[fieldKey] = this.cloneValue(newValue);
      this.initiallyPopulatedFields.add(fieldKey);
    }

    if (isValueChanged) {
      this.resetChildDropDowns(fieldKey);
      const childConfig = this.configs.find(cfg => cfg.parent === fieldKey);
      if (childConfig) {
        this.activeDropDown.emit({ ...childConfig, value: newValue });
      }
    }

    this.emitChange();
    this.cd.detectChanges();
    this.isSaving = false;
  }

  private validateField(fieldKey: string): boolean {
    if (!this.form) return false;

    const control = this.form.get(fieldKey);
    if (!control) return false;

    if (control.invalid) {
      control.markAsTouched();
      return false;
    }

    return true;
  }

  private isValueChanged(
    fieldKey: string,
    newValue: any,
    originalValue: any,
    config: ICascadingConfig
  ): boolean {
    if (this.wasInitiallyEmpty[fieldKey] && newValue && !originalValue) {
      return true;
    }

    if (!newValue && !originalValue) return false;
    if ((newValue && !originalValue) || (!newValue && originalValue)) {
      return true;
    }

    const bindValueKey = config.bindvalue ?? 'id';
    const bindLabelKey = config.bindlabel ?? 'name';

    try {
      const relevantKeys = [bindValueKey, bindLabelKey, 'value'].filter(
        key =>
          Object.prototype.hasOwnProperty.call(newValue, key) ||
          Object.prototype.hasOwnProperty.call(originalValue, key)
      );

      for (const key of relevantKeys) {
        const newVal = newValue[key];
        const origVal = originalValue[key];
        if (JSON.stringify(newVal) !== JSON.stringify(origVal)) {
          return true;
        }
      }

      return false;
    } catch (error) {
      return true;
    }
  }

  private emitChange(): void {
    if (!this.form) return;

    const data: Record<string, any> = {};
    const formattedValues: Record<string, EditableInputValueChange> = {};

    this.configs.forEach(config => {
      if (!config) return;

      const key = config.key;
      const control = this.form.get(key);
      const currentValue = control?.value;
      const originalValue = this.wasInitiallyEmpty[key]
        ? null
        : this.originalMappedValues[key] ?? this.mappedInitialValues[key];
      const isEdited = this.editedStates[key];

      const bindLabelKey = config.bindlabel ?? 'label';
      const bindValueKey = config.bindvalue ?? 'value';

      const datasetValueName =
        currentValue && typeof currentValue === 'object'
          ? currentValue[bindLabelKey]
          : undefined;

      const datasetValueCode =
        currentValue && typeof currentValue === 'object'
          ? currentValue[bindValueKey]
          : undefined;

      const valueObject: EditableInputValueChange = {
        status: isEdited,
        previousValue: originalValue,
        newValue: currentValue,
        datasetValueName,
        datasetValueCode,
      };

      formattedValues[key] = valueObject;

      data[`${key}_status`] = isEdited;
      data[`${key}_previousValue`] = originalValue;
      data[`${key}_newValue`] = currentValue;
      data[`${key}_datasetValueName`] = datasetValueName;
      data[`${key}_datasetValueCode`] = datasetValueCode;
    });

    this.valueChange.emit({
      formValue: formattedValues,
      data,
    });
  }

  writeValue(obj: { [key: string]: any }): void {
    if (!obj || !this.form || this.isSaving) return;

    const newInitialValues: Record<string, any> = {};
    Object.keys(obj).forEach(key => {
      if (obj[key] !== null && obj[key] !== undefined) {
        const value = obj[key];
        newInitialValues[key] =
          typeof value === 'string' || typeof value === 'number'
            ? this.stripLeadingZeros(String(value))
            : value;
      }
    });
    this.initialValues = { ...this.initialValues, ...newInitialValues };

    Object.keys(obj).forEach(key => {
      const config = this.configs.find(cfg => cfg.key === key);
      if (!config || !this.items?.[key]) return;

      const rawValue = newInitialValues[key] ?? obj[key];
      const control = this.form.get(key);

      const wasInitiallyEmpty =
        rawValue === undefined ||
        rawValue === null ||
        (rawValue &&
          typeof rawValue === 'object' &&
          (rawValue.previousValue === null ||
            rawValue.previousValue === undefined));
      this.wasInitiallyEmpty[key] = wasInitiallyEmpty;

      if (wasInitiallyEmpty) {
        this.initiallyNullFields.add(key);
      } else {
        this.initiallyNullFields.delete(key);
      }

      let mappedValue = this.mapInitialValue(config, rawValue);
      if (!mappedValue && !wasInitiallyEmpty) {
        mappedValue = this.originalMappedValues[key] || null;
      }

      if (rawValue === null && this.initialValues[key]) {
        mappedValue = this.mapInitialValue(config, this.initialValues[key]);
        if (!mappedValue && this.initialValues[key]) {
          mappedValue = this.cloneValue(this.initialValues[key]);
        }
        this.wasInitiallyEmpty[key] = false;
        if (mappedValue) {
          this.initiallyNullFields.delete(key);
        }
      }

      if (control) {
        control.setValue(mappedValue);
        this.mappedInitialValues[key] = this.cloneValue(mappedValue);
        this.savedValues[key] = this.cloneValue(mappedValue);
        this.tempValues[key] = this.cloneValue(mappedValue);
        this.pendingValues[key] = this.cloneValue(mappedValue);

        // Set editedStates based on status if rawValue is EditableInputValueChange
        if (
          rawValue &&
          typeof rawValue === 'object' &&
          'status' in rawValue &&
          'newValue' in rawValue
        ) {
          this.editedStates[key] = rawValue.status;
        } else {
          // For values from initializedLocationKeys, assume not edited unless changed
          this.editedStates[key] = false;
        }

        if (
          mappedValue &&
          (!this.isInitialized || !this.initiallyPopulatedFields.has(key)) &&
          (!this.initiallyNullFields.has(key) || this.initialValues[key])
        ) {
          this.originalMappedValues[key] = this.cloneValue(mappedValue);
          this.initiallyPopulatedFields.add(key);
        }
      }
    });

    this.isInitialized = true;
    this.cd.detectChanges();
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: (value: unknown) => void): void {
    this._onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.form) {
      isDisabled ? this.form.disable() : this.form.enable();
    }
  }

  ngOnDestroy(): void {
    if (this.fetchDataSubscriptions) {
      this.fetchDataSubscriptions.forEach(sub => {
        if (sub) sub.unsubscribe();
      });
    }
  }
}
